import { type FC } from 'react';
import cn from 'classnames';
import { CountBadge } from '@/components/CountBadge/CountBadge';
import { Skeleton } from '@/components/Skeletons/Skeleton/Skeleton';
import { type IUseCallOrdersResult } from '@/hooks/useCallOrders';
import { type ICallOrdersTab } from '@/hooks/useCallOrdersTabs';
import { type TCallOrdersFilterKind } from '@/types/ICallOrdersFilter';
import { CallOrdersTable } from '../CallOrdersTable/CallOrdersTable';
import styles from './callOrdersTabs.module.scss';

interface ICallOrdersTabsProps {
  tabs: ICallOrdersTab[];
  activeKind: TCallOrdersFilterKind;
  /** Данные активного таба. */
  data: IUseCallOrdersResult;
  /** Текст пустого состояния активного таба. */
  emptyText: string;
  /** Показывать колонку «Клиент» — в выборке по номеру получателя. */
  showClient: boolean;
  /**
   * Ключ учётки звонящего — `uuid`, у прежнего бэкенда `id` (PAR-5189) —
   * либо `null`. Для метки «Заказ клиента».
   */
  accountId: string | number | null;
  onTabChange: (kind: TCallOrdersFilterKind) => void;
}

const TABS_LABEL = 'Списки заказов звонящего';

const getTabId = (kind: TCallOrdersFilterKind): string =>
  `call-orders-tab-${kind}`;

const getPanelId = (kind: TCallOrdersFilterKind): string =>
  `call-orders-panel-${kind}`;

/**
 * Полоса вкладок над таблицей заказов в модалке звонка: заказы аккаунта
 * клиента и заказы, где номер звонящего указан получателем. Компонент
 * «тупой» — состояние табов и загрузка живут в `useCallOrdersTabs`.
 */
export const CallOrdersTabs: FC<ICallOrdersTabsProps> = ({
  tabs,
  activeKind,
  data,
  emptyText,
  showClient,
  accountId,
  onTabChange
}) => (
  <section className={styles['call-orders-tabs']}>
    <div
      className={styles['call-orders-tabs__list']}
      role="tablist"
      aria-label={TABS_LABEL}
    >
      {tabs.map((tab) => {
        const isActive = tab.kind === activeKind;

        return (
          <button
            key={tab.kind}
            type="button"
            role="tab"
            id={getTabId(tab.kind)}
            aria-selected={isActive}
            aria-controls={getPanelId(tab.kind)}
            className={cn(styles['call-orders-tabs__tab'], {
              [styles['call-orders-tabs__tab_active']]: isActive
            })}
            onClick={() => {
              onTabChange(tab.kind);
            }}
          >
            <span className={styles['call-orders-tabs__label']}>
              {tab.label}
            </span>

            {tab.isCountLoading && (
              <Skeleton
                variant="rounded"
                className={styles['call-orders-tabs__count-skeleton']}
              />
            )}

            {!tab.isCountLoading && tab.count !== null && (
              <CountBadge
                count={tab.count}
                className={styles['call-orders-tabs__count']}
              />
            )}
          </button>
        );
      })}
    </div>

    <div
      className={styles['call-orders-tabs__panel']}
      role="tabpanel"
      id={getPanelId(activeKind)}
      aria-labelledby={getTabId(activeKind)}
    >
      <CallOrdersTable
        orders={data.orders}
        total={data.total}
        page={data.page}
        limit={data.limit}
        isLoading={data.isLoading}
        error={data.error}
        emptyText={emptyText}
        showClient={showClient}
        accountId={accountId}
        onPageChange={data.changePage}
        onRetry={data.refetch}
      />
    </div>
  </section>
);
